import { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import type { ConversationTurn, LanguageItem } from '@/api/types';
import { parseTurnMetadata } from '../../lib/metadata-utils';

interface ConversationReplayProps {
  turns: ConversationTurn[];
  itemsMap: Map<string, LanguageItem>;
}

const LEVEL_STARS = ['', '★', '★★', '★★★', '★★★★', '★★★★★'];

const STATUS_STYLE: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  learning: 'bg-amber-100 text-amber-700',
  recognized: 'bg-emerald-100 text-emerald-700',
  mastered: 'bg-green-100 text-green-700',
};

function ScoreBadge({ label, value }: { label: string; value: number }) {
  const color =
    value >= 8
      ? 'bg-emerald-100 text-emerald-700'
      : value >= 5
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700';

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${color}`}>
      {label}: {value}
    </span>
  );
}

function ItemChip({ itemId, usageType, item, isTargetHit = false }: {
  itemId: string;
  usageType: string;
  item: LanguageItem | undefined;
  isTargetHit?: boolean;
}) {
  const [showCard, setShowCard] = useState(false);

  const usageColors: Record<string, string> = {
    practiced: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    introduced: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    corrected: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
  };

  const usageIcon: Record<string, string> = {
    practiced: '📝',
    introduced: '🆕',
    corrected: '✏️',
  };

  const displayText = item?.text || itemId.slice(0, 8) + '…';

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setShowCard(true)}
      onMouseLeave={() => setShowCard(false)}
    >
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium cursor-default transition-colors ${usageColors[usageType] ?? 'bg-muted text-muted-foreground border-muted'} ${isTargetHit ? 'ring-1 ring-indigo-400 border-indigo-400' : ''}`}
      >
        {usageIcon[usageType] ?? '📝'} {displayText}{isTargetHit && <span className="ml-0.5 text-indigo-500">★</span>}
      </span>

      {/* Hover detail card */}
      {showCard && item && (
        <div className="absolute bottom-full right-0 mb-1.5 z-50 w-52 rounded-lg border bg-white shadow-lg p-3 space-y-2 pointer-events-none">
          {/* Item text */}
          <div className="font-semibold text-sm text-gray-900 leading-tight">
            {item.text}
          </div>

          {/* Definition if available */}
          {item.definition && (
            <p className="text-xs text-gray-500 leading-snug line-clamp-2">
              {item.definition}
            </p>
          )}

          {/* Meta badges row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Type */}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase">
              {item.item_type}
            </span>

            {/* Status */}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${STATUS_STYLE[item.status] ?? 'bg-gray-100 text-gray-600'}`}>
              {item.status}
            </span>

            {/* Usage type */}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${usageColors[usageType]?.split(' hover:')[0] ?? 'bg-gray-100 text-gray-600'}`}>
              {usageType}
            </span>
          </div>

          {/* Level stars */}
          {item.level > 0 && (
            <div className="flex items-center gap-1 text-[11px]">
              <span className="text-amber-500">{LEVEL_STARS[item.level] || `Lv.${item.level}`}</span>
              <span className="text-gray-400">Level {item.level}</span>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

export function ConversationReplay({ turns, itemsMap }: ConversationReplayProps) {
  if (!turns || turns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <MessageCircle className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">
          No conversation data available
        </p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          This session has no recorded conversation turns.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
      {turns.map((turn) => {
        const isUser = turn.role === 'user';
        return (
          <div
            key={turn.turn_index}
            className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
          >
            {/* Difficulty change badge */}
            {(() => {
              const meta = parseTurnMetadata(turn);
              if (!meta?.difficulty_adjustment || meta.difficulty_adjustment === 'none') return null;
              const isUp = meta.difficulty_adjustment === 'up';
              return (
                <div className={`text-xs px-2 py-0.5 rounded-full mb-1 inline-block ${isUp ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                  {isUp ? '↑ Difficulty Up' : '↓ Difficulty Down'}
                </div>
              );
            })()}

            {/* Role label */}
            <span className={`text-[10px] font-medium mb-1 ${isUser ? 'text-blue-500' : 'text-gray-400'}`}>
              {isUser ? 'You' : 'AI'}
            </span>

            {/* Chat bubble */}
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                isUser
                  ? 'bg-blue-500 text-white rounded-br-md'
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}
            >
              {turn.content}
            </div>

            {/* AI introduced items — show as chips if linked, fallback to text for old data */}
            {turn.role === 'assistant' && (() => {
              const introducedLinks = (turn.items ?? []).filter(l => l.usage_type === 'introduced');
              if (introducedLinks.length > 0) {
                return (
                  <div className="flex gap-1 flex-wrap mt-1.5">
                    {introducedLinks.map((link) => (
                      <ItemChip
                        key={link.item_id}
                        itemId={link.item_id}
                        usageType={link.usage_type}
                        item={itemsMap.get(link.item_id)}
                      />
                    ))}
                  </div>
                );
              }
              // Fallback for old conversations without turn_item_links
              const meta = parseTurnMetadata(turn);
              if (!meta?.new_items_introduced?.length) return null;
              return (
                <p className="text-xs text-gray-400 mt-1">
                  Introduced: {meta.new_items_introduced.join(', ')}
                </p>
              );
            })()}

            {/* User message annotations */}
            {isUser && (
              <div className="max-w-[85%] mt-1.5 space-y-1.5">
                {/* Score badges */}
                {(turn.score_accuracy != null ||
                  turn.score_fluency != null ||
                  turn.score_naturalness != null) && (
                  <div className="flex gap-1 justify-end flex-wrap">
                    {turn.score_accuracy != null && (
                      <ScoreBadge label="Accuracy" value={turn.score_accuracy} />
                    )}
                    {turn.score_fluency != null && (
                      <ScoreBadge label="Fluency" value={turn.score_fluency} />
                    )}
                    {turn.score_naturalness != null && (
                      <ScoreBadge label="Naturalness" value={turn.score_naturalness} />
                    )}
                  </div>
                )}

                {/* Comprehension indicator */}
                {turn.role === 'user' && (() => {
                  const meta = parseTurnMetadata(turn);
                  if (!meta?.comprehension_level) return null;
                  const colors = { high: 'bg-green-500', medium: 'bg-yellow-500', low: 'bg-red-500' };
                  return (
                    <div className="flex justify-end mt-0.5">
                      <span className={`inline-block w-2 h-2 rounded-full ${colors[meta.comprehension_level]}`}
                            title={`Comprehension: ${meta.comprehension_level}`} />
                    </div>
                  );
                })()}

                {/* AI feedback annotation */}
                {turn.feedback && (
                  <p className="text-[11px] text-muted-foreground italic text-right">
                    {turn.feedback}
                  </p>
                )}

                {/* Linked item chips */}
                {turn.items && turn.items.length > 0 && (() => {
                  const meta = parseTurnMetadata(turn);
                  const targetHits = new Set(meta?.target_items_hit ?? []);
                  return (
                    <div className="flex gap-1 justify-end flex-wrap">
                      {turn.items.map((link) => (
                        <ItemChip
                          key={link.item_id}
                          itemId={link.item_id}
                          usageType={link.usage_type}
                          item={itemsMap.get(link.item_id)}
                          isTargetHit={targetHits.has(link.item_id)}
                        />
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
